/**
 * Typed wrapper around `GET /dashboard/summary`. Mirrors the response
 * shape declared in `packages/api/src/handlers/dashboard.ts`.
 *
 * Memoized on `[api]` so dashboard's `useEffect([api.summary])` doesn't
 * re-fire on every render.
 */

import { useMemo } from 'react';
import { useApi } from '../api';
import type { ApiResult } from '../auth/api-client';

export interface DashboardSummary {
  periodStart: string;
  periodEnd: string;
  intakeCount?: number;
  averageIdentificationConfidence?: number;
  /** Keys are grade strings '1'..'10'. */
  gradeDistribution?: Record<string, number>;
  /** Cards currently placed in storage (placementState === 'STORED'). */
  cardCount: number;
  /** Active, non-retired cards without a reviewer-verified identity. */
  awaitingIdentificationCount: number;
  /** Active eBay card listings known to TCVS, linked or external-only. */
  listedCardCount: number;
  /** Current USD value of active eBay card listings, in cents. */
  listedCardValueMinor: number;
  /** eBay sales observed during the current UTC day. */
  salesToday: DashboardSalesTotal;
  /** eBay sales observed during the trailing seven UTC days. */
  salesLast7Days: DashboardSalesTotal;
  /** Paid, non-cancelled orders that are not yet fulfilled. */
  awaitingFulfillmentCount: number;
  /** Paid eBay order cards still physically stored and awaiting a TCVS pick. */
  soldAwaitingPickCount: number;
  /** Open eBay synchronization exceptions requiring attention. */
  unresolvedEbayExceptions: number;
  marketplaceFreshness: {
    inventoryUpdatedAt?: string;
    ordersUpdatedAt?: string;
  };
  generatedAt: string;
  /** Daily eBay sales for the last 30 UTC days. */
  salesVolume: DashboardSalesTrendPoint[];
  /** Daily eBay listing starts for the last 30 UTC days. */
  cardsListedVolume: DashboardCountTrendPoint[];
  /** Daily card captures for the last 30 UTC days. */
  cardCaptureVolume: DashboardCountTrendPoint[];
  /** Outlier-adjusted average interval between consecutive card captures. */
  averageCaptureInterval: DashboardTimingAverage;
  /** Same capture interval calculation scoped to the most recent run/batch. */
  lastBatchCaptureInterval: DashboardTimingAverage;
}

export interface DashboardCountTrendPoint {
  date: string;
  count: number;
}

export interface DashboardSalesTrendPoint {
  date: string;
  orderCount: number;
  cardCount: number;
  amountMinor: number;
}

export interface DashboardSalesTotal {
  orderCount: number;
  cardCount: number;
  amountMinor: number;
}

export interface DashboardTimingAverage {
  averageMs: number;
  sampleCount: number;
  outliersTrimmed: number;
}

export function useDashboardApi() {
  const api = useApi();
  return useMemo(
    () => ({
      summary: (): Promise<ApiResult<DashboardSummary>> =>
        api.request<DashboardSummary>('/dashboard/summary?view=sales', { method: 'GET' }),
    }),
    [api],
  );
}
